import { Injectable } from '@angular/core';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { getRegistry } from '@ngneat/elf';

import { Observable } from 'rxjs';

import { AuthUtils } from './auth.utils';

export enum UserRole {
  SuperUser = 1, // Administrator - Labor C
  SysAdmin = 2, // Sys Administrator - other super user admins

  CourseAdmin = 3, // CompAdmin ==> Admin of the company
  //Manager = 4, // Manager or Regional Exec
  //Person = 5, // Person / Student / Employee
  Student = 5, // Person / Student / Employee
  //External = 9, // External User

  Advisor = 6, // Advisor / Reseller / Supervisor
  Supervisor = 11, // Advisor / Reseller / Supervisor
  Translator = 7, // Translator
  //Sales = 8, // Registrar / Sales Person
  WORLD = 999, // Everyone else
  System = 888, // System > notifications from the system
}

export type User = {
  role: number;
  name: string;
  email: string;
  phone: string;
  firststart_done: number;
  /** 1 = forced password-change wall; only /activate?mode=set-password is reachable. */
  must_change_password?: number;
  user_id: number; // account_id
  status: number;
  company_id: number;
  company: string;
  person_id: number; // fid
  firststart: string;
  isapproved: number;
  isactivated: number;
  email_verified?: boolean;
  profile_image: string;
  country: string;
  lang: string;
  badges_earned: string;
  new_messages: number;
  /** Student switched the AI tutor off — supervisor validates manually. */
  ai_opted_out?: number;

  supervisor_id: number;
  supervisor_name: string;
  supervisor_avatar: string;
  supervisor_roomkey: string;
  /** Supervisor is restricted by an admin to the students assigned to him. */
  supervisor_only_assigned?: number;

  twin_id: number;
  twin_name: string;
  twin_avatar: string;

  // RBAC Permission System fields
  permissions: string[];
  roles: string[];
  is_admin: boolean;

  // Company access fields
  allowed_companies: Array<{
    id: number;
    name: string;
    is_primary: boolean;
    can_manage: boolean;
  }>;
  // Course access fields (for supervisors)
  allowed_courses: Array<{
    id: number;
    name: string;
    is_primary: boolean;
  }>;
  is_global_admin: boolean;
  primary_company_id: number | null;
  /** Company whose dictionary Clear Word uses (own, assigned, or system default). */
  clearword_company_id?: number | null;
  clearword_company?: string;
};

export type AppData = {
  version: number;
  appdata: string;
};

export type AuthState = {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  token_type: string;
  access_point: string;
  customer_key: string;
  // phone_id: string;
  user: User;
  appdata: AppData;
};

export type ReturnAuthState = {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  token_type: string;
  user: User;
  appdata: AppData;

  status: number;
  msg: string;
  error: string;

  student: any;
};

@Injectable({ providedIn: 'root' })
export class AuthRepository {
  $user: Observable<User | null>;
  $appData: Observable<AppData | null>;
  private readonly authStore;

  constructor() {
    this.authStore = createStore(
      { name: 'auth' },
      withProps<AuthState>({
        access_token: '',
        expires_in: 0,
        refresh_token: '',
        token_type: '',
        appdata: null,
        user: null,
        access_point: '',
        customer_key: '',
      })
    );

    this.$user = this.authStore.pipe(select((state: AuthState) => state.user));
    this.$appData = this.authStore.pipe(select((state: AuthState) => state.appdata));

    /*
        role: 0,
    name: '',
    email: '',
    firststart_done: 0,
    userid: 0,
    status: 0,
    company: '',
    fid: 0,
    firststart: '',
    isapproved: 0,
    isactivated: 0,
    profile_image: '',
    country: '',
 */

    persistState(this.authStore, {
      key: 'auth',
      storage: localStorageStrategy,
    });
  }

  getAccessToken() {
    return this.authStore.getValue().access_token;
  }

  getRefreshToken() {
    return this.authStore.getValue().refresh_token;
  }

  getValue(): AuthState {
    return this.authStore.getValue();
  }

  /*
  updateTokens(accessToken: string, refreshToken: string) {
    this.authStore.update(setProps({ accessToken, refreshToken }));
  }
  */

  setUser(user: User) {
    this.authStore.update((state) => ({
      ...state,
      user,
    }));
  }

  getUser(): User {
    return this.authStore.getValue().user;
  }

  getUserInitials(): string {
    const name = this.authStore.getValue().user.name;

    return name
      .split(' ') // Split the name by spaces
      .map((part) => part[0]) // Take the first letter of each part
      .join('') // Join them together
      .toUpperCase(); // Convert to uppercase
  }

  isLoggedIn(): boolean {
    try {
      const token = this.getAccessToken();
      if (token) {
        return !!AuthUtils.isTokenExpired(token);
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  save(auth: AuthState) {
    this.authStore.update((state) => ({
      ...auth,
    }));
  }

  logout() {
    //this.authStore.remove();
    getRegistry().forEach((store) => store.reset());
    this.clear();
  }

  clear() {
    this.authStore.update(
      setProps({
        access_token: '',
        expires_in: 0,
        refresh_token: '',
        token_type: '',
        user: null,
        appdata: null,
      })
    );
  }
}
